import { useEffect, useState } from 'react';
import { useLocale, useT } from '../i18n';
import { usePrice } from '../lib/currency';
import { fmtDateDow, fmtDateShort } from '../lib/format';
import {
  cardPriceIsFloor,
  daysOutBucket,
  daysUntil,
  floorsOf,
  gaEvent,
  saveEvent,
  savedStore,
  shownIsBuilt,
  undoSaved,
  unsaveEvent,
  useIntl,
  useLocalToday,
  useSaved,
  visitorIntl,
} from '../lib/saved';
import { lastVisit, noteView, type RecentItem } from '../lib/recent';
import { priceChange } from '../../shared/saved-price.mjs';
import ShareButton, { eventLabel } from './ShareButton';
import { showToast } from './Toast';
import { XIcon } from './icons';
import type { Event } from '../types';

/**
 * SAVE, KEPT ON THIS DEVICE (2026-10-19) — the controls. The list itself: src/lib/saved.ts.
 *
 * Every saved state shows after mount only (the store's server snapshot is empty), so the
 * prerendered page, the edge page and the first client render are the same markup; a control
 * keeps its size when it turns into "Saved", so nothing moves. No account, no email, no urgency:
 * saving says what it does and that clearing browser data removes it (the /saved page).
 *
 * TWO EVENTS ON THE EVENT PAGE. `event` is the event as built: what the lists record and compare
 * (the daily D1 price measures the same thing). `shown` is the event as the page shows it, after
 * the live HelloTickets refresh (EventDetail's displayEvent); `settled` = that refresh has
 * answered, or the event has none. Share quotes `shown`'s fees-included floor, the price on screen.
 * "Since you looked/saved" appears only while the two floors agree, and a save or a view records
 * whether they did (`seen`), so no line ever describes a price the visitor did not see.
 */

/**
 * `settled`, for this event only. EventDetail keeps its live price across an in-app move to another
 * event for one render (its reset runs after that render), so a fresh `settled` counts only once it
 * has held through a committed render for the same event: never a floor read off the last event.
 */
const useSettledFor = (id: string, settled: boolean) => {
  const [heldFor, setHeldFor] = useState<string | null>(null);
  useEffect(() => setHeldFor(settled ? id : null), [id, settled]);
  return settled && heldFor === id;
};

/** The heart, drawn in the icon set's 1.6 px stroke (src/components/icons.tsx); filled when saved. */
export const HeartIcon = ({ className, filled = false }: { className?: string; filled?: boolean }) => (
  <svg className={className} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.6} strokeLinejoin="round" aria-hidden>
    <path d="M12 20.25s-7.75-4.7-7.75-10.4A4.35 4.35 0 0 1 8.6 5.5c1.45 0 2.6.72 3.4 1.9.8-1.18 1.95-1.9 3.4-1.9a4.35 4.35 0 0 1 4.35 4.35c0 5.7-7.75 10.4-7.75 10.4Z" />
  </svg>
);

/**
 * Save or unsave this event, and say what happened (a removal can be undone for a few seconds).
 * `seenFor(intl)`: the price this save records is the one in front of the visitor (SavedItem.seen).
 */
const useToggleSave = (event: Event, seenFor: (intl: boolean) => boolean) => {
  const t = useT();
  const saved = useSaved();
  const isSaved = saved.some((s) => s.id === event.id);
  const cannot = () => showToast({ message: t('Can’t save in this browser. It may be blocking site storage.') });
  const toggle = async (known: boolean | null) => {
    const params = { category: event.category, days_out_bucket: daysOutBucket(event.date) };
    if (isSaved) {
      const before = savedStore.get();
      if (!unsaveEvent(event.id)) return cannot();
      gaEvent('unsave_event', params);
      showToast({ message: t('Removed from your saved events.'), action: { label: t('Undo'), run: () => void undoSaved(before) } });
      return;
    }
    const intl = known ?? (await visitorIntl());
    if (!saveEvent(event, intl, seenFor(intl))) return cannot();
    gaEvent('save_event', params);
    showToast({
      message: t('Saved on this device. We check its price every day. No account, no email.'),
      link: { to: '/saved', label: t('See saved') },
    });
  };
  return { isSaved, toggle };
};

const saveLabel = (t: ReturnType<typeof useT>, event: Event, locale: string) =>
  t('Save {name}, {date}', { name: event.artist ?? event.name, date: fmtDateDow(event.date, locale) });

/**
 * The hero's Save: icon on phones, icon and word from sm up, a fixed width so "Saved" never
 * shifts. Its accessible name is the word it shows plus the event ("Saved KATSEYE, Wed, Oct 28",
 * pressed), so the visible label is always part of the name (WCAG 2.5.3, label in name).
 */
export const SaveButton = ({ event, intl, seen }: { event: Event; intl: boolean | null; seen: boolean }) => {
  const t = useT();
  const locale = useLocale();
  const { isSaved, toggle } = useToggleSave(event, () => seen);
  return (
    <button type="button" data-save="" aria-pressed={isSaved} onClick={() => void toggle(intl)} className="group/act grid h-11 min-w-11 place-items-center rounded-full">
      <span
        className={`inline-flex h-9 min-w-9 items-center justify-center gap-1.5 rounded-full border bg-background/90 px-2.5 text-[13px] font-medium shadow-sm backdrop-blur transition-colors sm:min-w-[94px] sm:px-3.5 ${
          isSaved ? 'border-primary/50 text-primary' : 'border-border text-foreground group-hover/act:border-foreground/30'
        }`}
      >
        <HeartIcon className="size-[17px]" filled={isSaved} />
        <span className="sr-only sm:not-sr-only">{isSaved ? t('Saved') : t('Save')}</span>
        <span className="sr-only"> {eventLabel(event, locale)}</span>
      </span>
    </button>
  );
};

/**
 * Save + Share at the hero's top right, on every screen size (one pair, so desktop never shows two
 * Save buttons and phones keep the first "Get tickets" where it was). A show today or tomorrow gets
 * Share only: there is no waiting game left to watch. Also records this page in Recently viewed —
 * again once the live refresh has answered, to note whether the recorded floor was the one shown.
 */
export const HeroActions = ({ event, shown, settled }: { event: Event; shown: Event; settled: boolean }) => {
  const intl = useIntl();
  const today = useLocalToday();
  const live = useSettledFor(event.id, settled);
  const seen = shownIsBuilt(event, shown, live, intl);
  useEffect(() => {
    if (intl !== null && !event.partial) noteView(event, intl, seen);
  }, [event, intl, seen]);
  const soon = daysUntil(event.date, today) <= 1;
  return (
    <div className="float-right -mr-2 -mt-2 ml-2 flex items-center sm:-mr-2.5 sm:-mt-2.5 sm:gap-1.5">
      {!soon && <SaveButton event={event} intl={intl} seen={seen} />}
      <ShareButton event={event} from={floorsOf(shown, false).floor} />
    </div>
  );
};

/**
 * "Down $27.60 since you saved it (Oct 16)" / "since you looked": inside the hero's "Prices
 * checked" line (EventDetail), on a return visit. Only when
 * - the fees-included floor on screen is the built one this compares (the live refresh has
 *   answered and agrees: otherwise the line would contradict the price beside it);
 * - the earlier price was also the one on screen then (`seen`), taken with the same storefront rule;
 * - both prices include fees, and the price moved.
 * Rises read the same plain way. Its own line on phones, beside the stamp from sm up. The fee basis is
 * named on every screen ("all-in" on phones, "fees included" from sm up) and stays with the date. On
 * a phone the stamp line above it truncates, but this one wraps (balanced, two lines in a long
 * language on a narrow phone) rather than cut its basis off.
 */
export const SinceYouLooked = ({ event, shown, settled }: { event: Event; shown: Event; settled: boolean }) => {
  const t = useT();
  const price = usePrice();
  const locale = useLocale();
  const intl = useIntl();
  const saved = useSaved();
  const live = useSettledFor(event.id, settled);
  const [visit, setVisit] = useState<RecentItem | null>(null);
  useEffect(() => {
    if (intl !== null && !event.partial) setVisit(lastVisit(event.id));
  }, [event.id, event.partial, intl]);
  if (intl === null || event.partial || !shownIsBuilt(event, shown, live, intl)) return null;
  const now = floorsOf(event, intl).floor;
  const mine = saved.find((s) => s.id === event.id);
  const prev = visit?.id === event.id ? visit : null;
  const fromSave = mine && mine.seen === true && !!mine.intl === intl ? priceChange(mine.floorAtSave, now) : null;
  const fromView = prev && prev.seen === true && !!prev.intl === intl ? priceChange(prev.floorAtView, now) : null;
  let text = '';
  let down = false;
  if (mine && fromSave) {
    down = fromSave < 0;
    const vars = { amount: price(Math.abs(fromSave)), date: fmtDateShort(mine.savedAt, locale) };
    text = down ? t('Down {amount} since you saved it ({date})', vars) : t('Up {amount} since you saved it ({date})', vars);
  } else if (prev && fromView) {
    down = fromView < 0;
    const vars = { amount: price(Math.abs(fromView)), date: fmtDateShort(prev.viewedAt, locale) };
    text = down ? t('Down {amount} since you looked ({date})', vars) : t('Up {amount} since you looked ({date})', vars);
  }
  if (!text) return null;
  return (
    <>
      <span className="hidden sm:inline"> · </span>
      <span data-since="" className={`block font-medium max-sm:whitespace-normal max-sm:[text-wrap:balance] sm:inline ${down ? 'text-pos' : 'text-neg'}`}>
        {text}
        <span className="whitespace-nowrap font-normal text-muted-foreground">
          {' · '}
          <span className="sm:hidden">{t('all-in')}</span>
          <span className="hidden sm:inline">{t('fees included')}</span>
        </span>
      </span>
    </>
  );
};

const NOTE_KEY = 'tixinfo.sharednote.v1';

/**
 * Someone sent this page (?utm_source=share, from the Share button): one quiet, dismissible line
 * under the hero offering to save it. No modal; gone once saved or closed; never for a show today
 * or tomorrow.
 */
export const SharedNote = ({ event, shown, settled }: { event: Event; shown: Event; settled: boolean }) => {
  const t = useT();
  const today = useLocalToday();
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const live = useSettledFor(event.id, settled);
  const seen = shownIsBuilt(event, shown, live, intl);
  const { isSaved, toggle } = useToggleSave(event, () => seen);
  useEffect(() => {
    let closed = false;
    try {
      closed = (sessionStorage.getItem(NOTE_KEY) ?? '').split(',').includes(event.id);
    } catch {
      /* no storage: the note can come back */
    }
    setOpen(!closed && new URLSearchParams(window.location.search).get('utm_source') === 'share');
  }, [event.id]);
  if (!open || isSaved || daysUntil(event.date, today) <= 1) return null;
  const close = () => {
    setOpen(false);
    try {
      const was = (sessionStorage.getItem(NOTE_KEY) ?? '').split(',').filter(Boolean);
      sessionStorage.setItem(NOTE_KEY, [...was, event.id].slice(-20).join(','));
    } catch {
      /* closing still works for this page */
    }
  };
  return (
    <div className="mt-3 flex items-center gap-1 rounded-xl border border-primary/25 bg-primary/[0.06] py-0.5 pl-3.5 pr-0.5 text-[13px] text-foreground/90">
      {/* to find it again, never "to watch its price": no line invites waiting (2026-10-19, owner) */}
      <p className="min-w-0 flex-1 py-2 [text-wrap:pretty]">{t('Sent to you? Save it to find it again.')}</p>
      <button
        type="button"
        onClick={() => void toggle(intl)}
        className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-lg px-2.5 font-semibold text-primary underline-offset-4 hover:underline"
      >
        <HeartIcon className="size-4" />
        {t('Save')}
      </button>
      <button
        type="button"
        onClick={close}
        aria-label={t('Close')}
        className="grid size-11 shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:text-foreground"
      >
        <XIcon className="size-4" />
      </button>
    </div>
  );
};

/**
 * The heart on an event card: a sibling of the card's link (never inside it), over the photo's
 * bottom-right corner, where no chip sits. The overlay repeats the card photo's aspect ratio
 * (src/components/EventCard.tsx: aspect-[2/1] sm:aspect-[16/10]) so the heart stays on the photo
 * at every width; it lets every click but the heart's through to the card. A card shows the
 * built headline price: when that is the floor a save records, the saved price counts as seen.
 */
export const CardSaveButton = ({ event, poster = false }: { event: Event; poster?: boolean }) => {
  const t = useT();
  const locale = useLocale();
  const { isSaved, toggle } = useToggleSave(event, (intl) => cardPriceIsFloor(event, intl));
  return (
    // the poster card (2026-10-22): the heart in the photo's top-right corner
    <div className={poster ? "pointer-events-none absolute right-1 top-1 size-11" : "pointer-events-none absolute inset-x-px top-px aspect-[2/1] sm:aspect-[16/10]"}>
      <button
        type="button"
        data-card-save=""
        aria-pressed={isSaved}
        onClick={() => void toggle(null)}
        className={poster ? "pointer-events-auto absolute inset-0 grid size-11 place-items-center rounded-full" : "pointer-events-auto absolute bottom-0.5 right-0.5 grid size-11 place-items-center rounded-full"}
      >
        <span
          className={`grid size-8 place-items-center rounded-full bg-background/90 ring-1 backdrop-blur-sm transition-colors ${
            isSaved ? 'text-primary ring-primary/50' : 'text-foreground/80 ring-border hover:text-foreground'
          }`}
        >
          <HeartIcon className="size-4" filled={isSaved} />
        </span>
        <span className="sr-only">{saveLabel(t, event, locale)}</span>
      </button>
    </div>
  );
};
